// The shortlist's search: the engine's best lines from the position the
// review is on, and the move played from there, scored. The stored scan
// answers first when it has the position; the engine is asked live only
// when it does not. A new position supersedes the search, stepping quickly
// starts one search where the reader stops, and the search runs only while
// someone holds it (`acquire`). Pure: no React, no DOM.
import type { Chess } from '@chess/api-chess';
import type { Engine, EngineLine, EngineScore } from '@chess/api-engine';
import { positionAt, viewPly, type Review, type ReviewState } from '@chess/api-review';
import type { ShortlistReading } from '@chess/api-review/board';
import { positionAt as scanPositionAt, type Analysis } from '@chess/api-analysis';
import { DEFAULT_MISTAKE_RULES, type Fen, type Uci } from '@chess/api-model';
import { describe, negate, verdictFor, verdictOfGrade, type Verdict } from './verdict.ts';

/** How hard the engine looks: whichever of these it reaches first. */
export const SEARCH = { depth: 14, moveTimeMs: 4000 };
export const LINES = 3;
/** How long stepping must pause before a new search starts. */
export const STEP_PAUSE_MS = 250;

export interface Candidate {
  rank: number;
  uci: Uci;
  san: string;
  score: EngineScore;
  continuation: string;
  valid: boolean;
}

export interface Played {
  uci: Uci;
  san: string;
  score?: EngineScore;
  verdict?: Verdict;
}

export type ShortlistStatus = 'idle' | 'searching' | 'scoring' | 'done' | 'over' | 'failed';

/** The shortlist's reading of the position the review is on. */
export interface ShortlistState {
  fen?: Fen;
  /** The initial position: nothing is shown and no arrows are drawn. */
  initial: boolean;
  sideToMove: 'White' | 'Black';
  depth: number;
  candidates: Candidate[];
  played?: Played;
  status: ShortlistStatus;
  error?: string;
}

export const INITIAL_SHORTLIST: ShortlistState = { initial: false, sideToMove: 'White', depth: 0, candidates: [], status: 'idle' };

/**
 * The game ply of the position shown, when it is one of the game's: the ply
 * looked back at (`browse`), else the review's ply. Undefined on the
 * reader's own line past its branch, where nothing was played and the
 * stored scan does not apply.
 */
export function gamePlyOf(state: ReviewState): number | undefined {
  const ply = viewPly(state);
  return state.exploring && ply > state.exploring.fromPly ? undefined : ply;
}

/**
 * What the board and the score bar are told (`review.shortlist`): the best
 * line's move and the played move. While the theory takes the best-moves
 * place the book's moves replace the engine's, so no best move is sent.
 */
export function readingOf(s: ShortlistState, theoryActive?: boolean): ShortlistReading {
  const best = theoryActive ? undefined : s.candidates.find((c) => c.rank === 1);
  return {
    fen: s.fen,
    best: s.fen && best ? { uci: best.uci, score: { cp: best.score.cp, mate: best.score.mate } } : undefined,
    played: s.fen && s.played ? { uci: s.played.uci, verdict: s.played.verdict } : undefined,
    // The best move's search is over once the played move is being scored, or all is done.
    settled: s.fen ? s.status === 'scoring' || s.status === 'done' || s.status === 'over' : undefined,
  };
}

export interface ShortlistSearchOptions {
  engine: Engine;
  chess: Chess;
  analysis: Analysis;
  /** The review to follow while the search is held. */
  review: Pick<Review, 'state' | 'subscribe'>;
  /** Told the whole state after every change. */
  onState(state: ShortlistState): void;
  /** Told when a search fails. */
  onError?(error: unknown): void;
  /** How long stepping must pause before a new search starts; `STEP_PAUSE_MS` by default. */
  pauseMs?: number;
  /** How hard the engine looks; `SEARCH` by default. */
  search?: { depth?: number; moveTimeMs?: number };
}

export interface ShortlistSearch {
  /** Analyse a review state now, superseding any running search. */
  analyse(state: ReviewState): Promise<void>;
  /** Hold the search: the first holder starts following the review, the last one to let go stops it. */
  acquire(): () => void;
  /** The current reading. */
  state(): ShortlistState;
  /** How many hold the search. */
  users(): number;
}

export function createShortlistSearch(options: ShortlistSearchOptions): ShortlistSearch {
  const { engine, chess, analysis, review } = options;
  const pauseMs = options.pauseMs ?? STEP_PAUSE_MS;
  const search = options.search ?? SEARCH;
  let current: ShortlistState = INITIAL_SHORTLIST;
  let controller: AbortController | undefined;
  let run = 0;

  const set = (patch: Partial<ShortlistState>) => {
    current = { ...current, ...patch };
    options.onState(current);
  };

  // Analyse the position the review is on; a new position supersedes the search.
  // The stored scan answers first when it has the position; the engine is
  // asked live only when it does not, and the scan pauses meanwhile.
  const analyse = async (state: ReviewState): Promise<void> => {
    controller?.abort();
    const fen = positionAt(state);
    const mine = ++run;
    if (!fen || !state.game) {
      set({ fen: undefined, initial: false, candidates: [], played: undefined, depth: 0, status: 'idle' });
      return;
    }
    // The played move and the scan are read at the position shown, looked
    // back at or not; on the reader's own line nothing was played from here,
    // and the stored scan does not apply.
    const ply = gamePlyOf(state);
    // The initial position: nothing to show and no arrows.
    if (ply === 0) {
      set({ fen: undefined, initial: true, candidates: [], played: undefined, depth: 0, status: 'idle' });
      return;
    }
    controller = new AbortController();
    const signal = controller.signal;
    const playedMove = ply === undefined ? undefined : state.game.moves[ply];
    // The user's mistake rules, as the review's scan carries them.
    const rules = state.scan?.rules ?? DEFAULT_MISTAKE_RULES;
    const played: Played | undefined = playedMove ? { uci: playedMove.uci, san: playedMove.san } : undefined;
    const sideToMove = fen.split(' ')[1] === 'b' ? 'Black' : 'White';
    set({ fen, initial: false, sideToMove, candidates: [], played, depth: 0, status: 'searching', error: undefined });
    // A finished position has no moves to shortlist.
    if (chess.legalMoves(fen).length === 0) {
      set({ status: 'over' });
      return;
    }

    // What the scan already knows about this position, if anything.
    const scan = ply === undefined ? undefined : await analysis.get(state.game.id).catch(() => undefined);
    if (mine !== run) return;
    const stored = ply === undefined ? undefined : scanPositionAt(scan, ply);
    if (ply !== undefined && stored && stored.lines.length > 0) {
      const candidates = stored.lines.map((line, i) => ({ rank: i + 1, uci: line.uci, score: line.score, ...describe(chess, fen, line.pv) }));
      const bestLine: EngineLine = { rank: 1, depth: stored.depth, score: stored.lines[0].score, pv: stored.lines[0].pv };
      const next = scanPositionAt(scan, ply + 1);
      const score = next ? negate(next.score) : undefined;
      // The app's grade of the played move (the user's rules), as the scoresheet shows it.
      const grade = playedMove ? state.scan?.grade(ply + 1, playedMove, ply % 2 === 0 ? 'white' : 'black') : undefined;
      const verdict: Verdict | undefined = grade ? verdictOfGrade(grade) : played && score ? verdictFor(bestLine, score, played.uci, rules) : undefined;
      set({ depth: stored.depth, candidates, played: played ? { ...played, score, verdict } : undefined, status: 'done' });
      return;
    }

    const release = analysis.hold();
    try {
      // Stop whatever the engine is doing before asking for this position, so
      // no update from the old search is read as an answer about the new one.
      await engine.stop();
      if (mine !== run) return;
      let lines: EngineLine[] = [];
      for await (const update of engine.analyse(fen, { ...search, multiPv: LINES, signal })) {
        if (mine !== run) return;
        const candidates = update.lines.slice(0, LINES).map((line) => ({
          rank: line.rank,
          uci: line.pv[0],
          score: line.score,
          ...describe(chess, fen, line.pv),
        }));
        if (candidates.some((c) => !c.valid)) continue; // stale: about another position
        lines = update.lines;
        const best = lines.find((l) => l.rank === 1);
        const inList = played ? lines.find((l) => l.pv[0] === played.uci) : undefined;
        set({
          depth: update.depth,
          candidates,
          played: played && inList ? { ...played, score: inList.score, verdict: verdictFor(best, inList.score, played.uci, rules) } : played,
        });
      }
      if (mine !== run) return;

      // The played move is scored by the search when it is one of the lines;
      // otherwise the position after it is searched once, from the other side.
      const best = lines.find((l) => l.rank === 1);
      if (played && !lines.some((l) => l.pv[0] === played.uci)) {
        const after = ply === undefined ? undefined : state.game.positions[ply + 1];
        if (after && best) {
          set({ status: 'scoring' });
          const reply = await engine.evaluate(after, { ...search, multiPv: 1, signal });
          if (mine !== run) return;
          const score = reply.lines[0] ? negate(reply.lines[0].score) : undefined;
          set({ played: { ...played, score, verdict: verdictFor(best, score, played.uci, rules) } });
        }
      }
      set({ status: 'done' });
    } catch (error) {
      if (mine !== run || signal.aborted) return;
      set({ status: 'failed', error: error instanceof Error ? error.message : String(error) });
      options.onError?.(error);
    } finally {
      release();
    }
  };

  // Following the review: analyse the position it is on, again on each step.
  const follow = (): (() => void) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    void analyse(review.state());
    // Stepping quickly through plies starts one search for where the
    // reader stops, not one per ply: the running search is cut at once
    // and the new one waits for the stepping to pause.
    const offReview = review.subscribe((state) => {
      controller?.abort();
      clearTimeout(timer);
      timer = setTimeout(() => void analyse(state), pauseMs);
    });
    // When the scan reaches the position on the board, show its lines in place of "Searching…".
    const offAnalysis = analysis.subscribe((event) => {
      if (event.type === 'complete' && event.gameId === review.state().gameId && current.status !== 'done') {
        void analyse(review.state());
      }
    });
    return () => {
      clearTimeout(timer);
      offReview();
      offAnalysis();
      controller?.abort();
    };
  };

  // It runs while someone needs the engine's reading of the board: the panel
  // (mounted), or a visible score bar while the panel is replaced, as after a
  // solved puzzle, where the board stands on the solution line, beyond the
  // scan. Held by count, so two users run one search, not two. While a puzzle
  // is being solved both are hidden, so the engine is left to the solver.
  let users = 0;
  let stopFollowing: (() => void) | undefined;
  const acquire = (): (() => void) => {
    if (users++ === 0) stopFollowing = follow();
    let held = true;
    return () => {
      if (!held) return;
      held = false;
      if (--users === 0) {
        stopFollowing?.();
        stopFollowing = undefined;
      }
    };
  };

  return { analyse, acquire, state: () => current, users: () => users };
}
